import PubSub from 'pubsub-js';
import * as THREE from 'three';
import _ from 'underscore';

import { userStateAdapter } from '../react/state/user-state/user-state-adapter';
import { ToolName } from '../services/metadata/meta-types';
import { isDefined } from '../utils/variables';
import { AssetManager } from './asset-manager';
import { Geometry } from './elements/geometry';
import { publish, SubscriptionTopic } from './messaging/pubsub';
import { AsbuiltGeometryTool } from './tools/asbuilt-geometry-tool';
import { CameraInputTool } from './tools/camera-input-tool';
import { DesignTool } from './tools/design-tool';
import InspectionTool from './tools/inspection-tool';
import { IntersectionTool } from './tools/intersection-tool';
import { MeasureTool } from './tools/measure-tool';
import { OnDemandTool } from './tools/on-demand-tool';
import { Orient360PoiTowardsNorthTool } from './tools/orient-360-poi-towards-north';
import { OrthophotoComparisonTool } from './tools/orthophoto-comparison-tool';
import { PoiTool } from './tools/poi-tool';
import { ProfileSectionTool } from './tools/profile-section-tool';
import { ReferencePointTransformTool } from './tools/reference-point-transform-tool';
import { ReportTool } from './tools/report-tool';
import { SelectTool } from './tools/select-tool';
import { Tool } from './tools/tool';
import TransformTool from './tools/transform-tool';
import { VolumeCalculatorTool } from './tools/volume-calculator-tool';
import { VolumeInspectorTool } from './tools/volume-inspector-tool';
import { WmsTool } from './tools/wms-tool';
import { InputHandlerInterface, ToolInterface } from './types';
import {
  InputEventDataExtractor,
  KeyInputData,
  MouseButtonType,
  MouseInputData,
} from './utilities/input-event/input-event-data-extractor';

interface InputEventCallbacks {
  [k: string]: (...args: any[]) => any;
}

// Add any keys here that we want to support key events for.
export type InputKeys =
  | 'Control'
  | 'Delete'
  | 'Enter'
  | 'Escape'
  | 'Shift'
  | 'PageUp'
  | 'PageDown'
  | 'ArrowUp'
  | 'ArrowDown'
  | 'q'
  | 'w'
  | 'e'
  | 'r'
  | 'a'
  | 'm'
  | 'c'
  | 'g'
  | 'i'
  | 'h'
  | 'l'
  | 'o'
  | 'p'
  | 's'
  | 't'
  | 'u'
  | 'y'
  | 'z';

// Define custom event map for InputHandler
type InputHandlerEventMap = THREE.Object3DEventMap & {
  // eslint-disable-next-line @typescript-eslint/ban-types
  animate: {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  updateUI: {};
};

export class InputHandler
  extends THREE.Object3D<InputHandlerEventMap>
  implements InputHandlerInterface
{
  static get instance() {
    return InputHandler._instance;
  }

  private static _instance: InputHandler = new InputHandler();

  private tools: { [key: string]: Tool };
  private tool: ToolInterface | undefined;
  private inputEventDataExtractor: InputEventDataExtractor;
  domElement?: HTMLElement;

  private domElementEventListeners: InputEventCallbacks = {};
  private inputHandlerEventListeners: InputEventCallbacks = {};
  private documentEventListener: InputEventCallbacks = {};

  private mouseInBounds = true;
  shiftIsDown = false;
  ctrlIsDown = false;
  altIsDown = false;
  doubleClick = false;
  /**
   * Time in milliseconds when to trigger double click.
   * For reference, de default double click speed in widnows is 480ms.
   */
  doubleClickSensitivity = 300;
  mouseDownTime = 0;

  private constructor() {
    super();
    this.inputEventDataExtractor = new InputEventDataExtractor();

    this.tools = {};
    // Timeout because the export order is not working correctly. The try/catch
    // guards against circular-import races in test environments where async ESM
    // loading can schedule this callback before all live bindings are resolved.
    setTimeout(() => {
      try {
        this.ensureToolsInitialized();
      } catch {
        // Tool initialization failed (e.g. circular import not yet resolved).
        // Call ensureToolsInitialized() explicitly once all modules are loaded.
      }
    }, 0);

    this.populateEventListeners();

    for (const event in this.inputHandlerEventListeners) {
      this.addEventListener(
        event as keyof InputHandlerEventMap,
        this.inputHandlerEventListeners[event],
      );
    }
  }

  ensureToolsInitialized() {
    this.tools = {};
    const factories: (() => Tool)[] = [
      () => new TransformTool(this),
      () => new PoiTool(this),
      () => new ProfileSectionTool(this),
      () => new SelectTool(this),
      () => new MeasureTool(this),
      () => new InspectionTool(this),
      () => new CameraInputTool(this),
      () => new VolumeInspectorTool(this),
      () => new OnDemandTool(this),
      () => new OrthophotoComparisonTool(this),
      () => new ReferencePointTransformTool(this),
      () => new DesignTool(this),
      () => new AsbuiltGeometryTool(this),
      () => new VolumeCalculatorTool(this),
      () => new ReportTool(this),
      () => new WmsTool(this),
      () => new Orient360PoiTowardsNorthTool(this),
      () => new IntersectionTool(this),
    ];
    for (const factory of factories) {
      try {
        this.addTool(factory());
      } catch {
        // Skip tools whose circular-import bindings are still unresolved.
      }
    }
    this.dispatchEvent({ type: 'animate' });
  }

  /**
   *  Animate the tool if selected.
   */
  onAnimate() {
    if (this.tool) {
      this.tool.onAnimate();
    }
    // Moves the camera between input events, e.g. while flying. The tools are added after a timeout.
    (this.tools.camera as Tool | undefined)?.onAnimate();
  }

  setCursor(cursor: string) {
    if (isDefined(this.domElement)) {
      this.domElement.style.cursor = cursor;
    }
  }

  currentTool(): ToolInterface | undefined {
    return this.tool;
  }

  setScene(baseScene: THREE.Scene) {
    _.values(this.tools).map((tool: Tool) => baseScene.add(tool));
  }

  freezeTranslate() {
    const selected = AssetManager.instance.getSelected();
    return selected && selected.freezeState.translate;
  }

  freezeRotate() {
    const selected = AssetManager.instance.getSelected();
    return selected && selected.freezeState.rotateZ;
  }

  freezeScale() {
    const selected = AssetManager.instance.getSelected();
    return selected && selected.freezeState.scale;
  }

  getMeta() {
    if (this.isTransformToolActive() || this.isSelectToolActive()) {
      return AssetManager.instance.getSelectedMeta();
    } else {
      return this.tool && this.tool.getMeta();
    }
  }

  getReadMeta() {
    if (this.isTransformToolActive() || this.isSelectToolActive()) {
      return AssetManager.instance.getSelectedReadMeta();
    }
  }

  getTransformMode() {
    const transformTool = this.tools.transform;
    if (typeof (transformTool as any).getMode === 'function') {
      return (transformTool as any).getMode();
    }
  }

  /** The name of the tool in use. */
  getToolName() {
    return this.tool?.name;
  }

  isTransformToolActive() {
    return this.tool && this.tool.name === 'transform';
  }

  isAsbuiltToolActive() {
    return this.tool?.name === 'asbuilt-geometry';
  }

  isSelectToolActive() {
    return this.tool && this.tool.name === 'select';
  }

  isMeasureToolActive() {
    return this.tool && this.tool.name === 'measure';
  }

  isInspectionToolActive() {
    return this.tool && this.tool.name === 'inspection';
  }

  isVolumeToolActive() {
    return this.tool && this.tool.name === 'volume-calculator';
  }

  isOnDemandToolActive() {
    return this.tool && this.tool.name === 'on-demand';
  }

  isVolumeInspectorToolActive() {
    return this.tool?.name === 'volume-inspector';
  }

  isOrthophotoComparisonToolActive() {
    return this.tool?.name === 'orthophoto-comparison';
  }

  isReferencePointTransformToolActive() {
    return this.tool?.name === 'reference-point-transform';
  }

  isGeometryEditorToolActive() {
    return this.tool?.name === 'design';
  }

  isReportToolActive() {
    return this.tool?.name === 'report';
  }

  isAsbuiltGeometryActive() {
    return this.tool?.name === 'asbuilt-geometry';
  }

  isWmsToolActive() {
    return this.tool?.name === 'wms';
  }

  isEditing() {
    return this.tool?.isEditing() ?? false;
  }

  toggleAssetMeasuring(state: boolean) {
    if (this.tool && (this.tool.name === 'measure' || this.tool.name === 'select')) {
      (this.tool as MeasureTool | SelectTool).assetMeasuring = state;
    }
  }

  /**
   * Gets the active tool if its snapping is set in the snapping dialog: the measure tool, the POI
   * tool, or the select tool when the points of the selected line or area can be moved.
   */
  getSnappingTool(): MeasureTool | PoiTool | SelectTool | undefined {
    const selected = AssetManager.instance.getSelected();

    switch (this.tool?.name) {
      case 'measure':
        return this.tool as MeasureTool;
      case 'poi':
        return this.tool as PoiTool;
      case 'select':
        return selected instanceof Geometry && selected.editable
          ? (this.tool as SelectTool)
          : undefined;
      default:
        return undefined;
    }
  }

  getAssetMeasuringState() {
    if (this.tool && (this.tool.name === 'measure' || this.tool.name === 'select')) {
      return (this.tool as MeasureTool | SelectTool).assetMeasuring;
    } else {
      return false;
    }
  }

  setTool(toolNames: string) {
    const t = toolNames.split('.');
    const oldTool = this.tool;

    let newTool = this.tools[t[0]];

    // Access control for specific tools.
    if (
      (toolNames === 'design' || toolNames === 'reference-point-transform') &&
      !userStateAdapter.getSnapshot().hasDesignToolAccessForCurrentProject()
    ) {
      publish(SubscriptionTopic.ShowDesignLicenseDialog);
      return;
    }

    // Do prevent tool from switching if there is data that may be lost
    if (this.tool instanceof DesignTool && this.tool.saved === false) {
      this.tool.openSaveConfirmationDialog(toolNames);
      return;
    }

    switch (t[0] as ToolName) {
      case 'transform': {
        const selectedAsset = AssetManager.instance.getSelected();
        if (!selectedAsset) {
          newTool = this.tools.none;
          break;
        }

        // @ts-ignore
        if (!selectedAsset.freezeState[t[1]]) {
          // @ts-ignore
          this.tools.transform.setMode(t[1]);
          if (newTool instanceof TransformTool) {
            newTool.attachAsset(selectedAsset);
          }
        }
        break;
      }
      case 'camera':
        newTool = this.tools.none;
        oldTool!.disable();
        newTool.enable();
        break;
      // Standard change tool behaviour
      case 'measure':
      case 'inspection':
      case 'orthophoto-comparison':
      case 'profile-section':
        oldTool?.disable();
        newTool.enable();
        break;
      // Change tool without deselecting asset (disabling select tool will deselect asset normally)
      case 'volume-calculator':
      case 'on-demand':
      case 'report':
      case 'volume-inspector':
      case 'reference-point-transform':
      case 'design':
      case 'wms':
        if (oldTool && oldTool.name !== newTool.name && oldTool.name !== 'select') {
          oldTool.disable();
        }
        break;
      default:
        if (oldTool && oldTool.name !== newTool.name) {
          oldTool.disable();
        }
        break;
    }

    if (oldTool && oldTool.name !== newTool.name) {
      newTool.enable();
    }

    this.tool = newTool;
    publish(SubscriptionTopic.SetTool, newTool);
  }

  /**
   *  Unset the current tool if it matches the target tool name and sets a replacement tool.
   *  @param {string} targetToolName - The name of the tool to unset.
   *  @param {string} replacementTool - The name of the tool to set as a replacement.
   */
  unsetTool(targetToolName: string, replacementTool: string) {
    if (this.tool?.name === targetToolName) {
      this.setTool(replacementTool);
    }
  }

  toggleTool(targetToolName: string, replacementTool: string) {
    this.setTool(this.tool?.name !== targetToolName ? targetToolName : replacementTool);
  }

  reset(newElement: HTMLElement) {
    this.disable();
    this.domElement = newElement;
    this.enable();
  }

  /** A way to manage removing the text area if a text area exist when moving our of the area. */
  abort() {
    // @ts-ignore
    if (this.tool && typeof this.tool.clearTooltip === 'function') {
      // @ts-ignore
      this.tool.clearTooltip();
    }
  }

  handleContextMenu(input: MouseInputData) {
    // Do not display context menu if camera has been moved.
    // (Right clicking, rotating and then releasing button on sphere)
    if (
      this.tool === undefined ||
      (this.tools.camera && (this.tools.camera as CameraInputTool).hasCameraMoved())
    ) {
      return false;
    }

    // Return if context menu should not be shown.
    const showContextMenu =
      input.button.type === MouseButtonType.RightButton && this.tool.showContextMenu(input);
    if (!showContextMenu) {
      return false;
    }

    PubSub.publish('skyviewContextMenu.UpdateItems', {
      items: (this.tool as any).getContextMenuItems(input),
      x: input.pointer.client.x + 1,
      y: input.pointer.client.y + 1,
    });

    return true;
  }

  enable() {
    if (!this.domElement) {
      return;
    }
    for (const event in this.domElementEventListeners) {
      this.domElement.addEventListener(event, this.domElementEventListeners[event]);
    }

    for (const event in this.documentEventListener) {
      document.addEventListener(event, this.documentEventListener[event]);
    }

    if (this.tool !== undefined) {
      this.tool.enable();
    }
  }

  disable() {
    if (!this.domElement) {
      return;
    }
    for (const event in this.domElementEventListeners) {
      this.domElement.removeEventListener(event, this.domElementEventListeners[event]);
    }

    for (const event in this.documentEventListener) {
      document.removeEventListener(event, this.documentEventListener[event]);
    }

    if (this.tool !== undefined) {
      this.tool.disable();
      this.tool = undefined;
    }
  }

  /**
   *  Declares all of the input events binding this to it.
   *
   *  The 'bind' function returns a new method, which nullifies any attempt to remove it as a listener in the future.
   *  By pre-declaring the bound functions, removing it later on works as intended
   */
  private populateEventListeners() {
    this.domElementEventListeners.contextmenu = (event) => {
      event.preventDefault();
    };

    if ('ontouchstart' in window) {
      this.domElementEventListeners.touchstart = this.onTouchStart.bind(this);
      this.domElementEventListeners.touchmove = this.onTouchMove.bind(this);
      this.domElementEventListeners.touchend = this.onTouchEnd.bind(this);
      this.domElementEventListeners.touchcancel = this.onTouchEnd.bind(this);
      this.domElementEventListeners.touchleave = this.onTouchEnd.bind(this);
    }

    this.domElementEventListeners.mousedown = this.onMouseDown.bind(this);
    this.domElementEventListeners.mousemove = this.onMouseMove.bind(this);
    this.domElementEventListeners.mouseenter = this.onMouseIn.bind(this);
    this.domElementEventListeners.mouseout = this.onMouseOut.bind(this);
    this.domElementEventListeners.mouseup = this.onMouseUp.bind(this);
    this.domElementEventListeners.mousewheel = this.onMouseWheel.bind(this);

    this.documentEventListener.keydown = this.onKeyDown.bind(this);
    this.documentEventListener.keyup = this.onKeyUp.bind(this);
    this.documentEventListener.mouseup = this.onGlobalMouseUp.bind(this);

    this.inputHandlerEventListeners.animate = this.onAnimate.bind(this);
  }

  private onTouchStart(e: TouchEvent) {
    e.preventDefault();

    const touchInputData = this.inputEventDataExtractor.extractTouchesEventData(e);
    if (this.tool && this.tool.onTouchStart(touchInputData)) {
      this.tools.camera.onTouchStart(touchInputData);
    }
  }

  private onTouchMove(e: TouchEvent) {
    e.preventDefault();

    const touchInputData = this.inputEventDataExtractor.extractTouchesEventData(e);
    if (this.tool && this.tool.onTouchMove(touchInputData)) {
      this.tools.camera.onTouchMove(touchInputData);
    }
    this.dispatchEvent({ type: 'animate' });
  }

  private onTouchEnd(e: TouchEvent) {
    const touchInputData = this.inputEventDataExtractor.extractTouchesEventData(e);

    if (this.tool && this.tool.onTouchEnd(touchInputData)) {
      this.tools.camera.onTouchEnd(touchInputData);
    }
  }

  onKeyUp(e: KeyboardEvent) {
    this.shiftIsDown = e.shiftKey;
    this.ctrlIsDown = e.ctrlKey;
    this.altIsDown = e.altKey;

    const input = this.inputEventDataExtractor.extractKeyEventData(e);
    this.tool?.onKeyUp(input);
    (this.tools.camera as Tool | undefined)?.onKeyUp(input);
  }

  onKeyDown(e: KeyboardEvent) {
    if (e.repeat) {
      return;
    }

    this.shiftIsDown = e.shiftKey;
    this.ctrlIsDown = e.ctrlKey;
    this.altIsDown = e.altKey;

    const input = this.inputEventDataExtractor.extractKeyEventData(e);

    if (this.mouseInBounds) {
      this.tool?.onKeyDown(input);
      // The camera, e.g. flying, takes the keys the tool doesn't use
      if (!input.handled) {
        (this.tools.camera as Tool | undefined)?.onKeyDown(input);
      }

      //Handle ctrl+a keypress so that it doesn't select all the text on screen.
      this.handleKeyPress(
        input,
        'a',
        () => {
          /*  */
        },
        'Control',
      );
    }

    this.handleKeyPress(input, 'Escape', () => {
      this.setTool('select');
    });
  }

  handleKeyPress(
    input: KeyInputData,
    key: InputKeys,
    action?: () => void,
    controlKey?: 'Control' | 'Alt' | 'Shift',
  ) {
    if (input.handled) {
      return;
    }

    const controlKeyPressed = () => {
      switch (controlKey) {
        case 'Control':
          // Note "metaKey" is for Mac. And it is supposed to work in most browsers.
          return input.ctrl || input.metaKey;
        case 'Alt':
          return input.alt;
        case 'Shift':
          return input.ctrl;
        default:
          return true;
      }
    };

    if (input.key.toLowerCase() === key.toLowerCase() && controlKeyPressed()) {
      input.e.preventDefault();
      input.e.stopPropagation();
      input.handled = true;
      action?.();
    }
  }

  private onMouseDown(e: MouseEvent) {
    e.preventDefault();

    const mouseInputData = this.inputEventDataExtractor.extractMouseEventData(e);

    const now = new Date().getTime();
    this.doubleClick = now - this.mouseDownTime < this.doubleClickSensitivity;

    if (this.doubleClick) {
      this.currentTool()?.onDoubleClick(mouseInputData);
    } else if (this.tool && this.tool.onMouseDown(mouseInputData)) {
      this.tools.camera.onMouseDown(mouseInputData);
    }

    this.mouseDownTime = now;
  }

  private onMouseMove(e: MouseEvent) {
    e.preventDefault();

    const mouseInputData = this.inputEventDataExtractor.extractMouseEventData(e);

    if (this.tool) {
      if (this.tool.onMouseMove(mouseInputData)) {
        this.tools.camera.onMouseMove(mouseInputData);
      }
    }

    this.dispatchEvent({ type: 'animate' });
    this.dispatchEvent({ type: 'updateUI' });
  }

  private onMouseIn(e: MouseEvent) {
    this.mouseInBounds = true;

    if (document.activeElement instanceof HTMLSelectElement) {
      document.activeElement.blur();
    }

    if (this.tool) {
      const mouseInputData = this.inputEventDataExtractor.extractMouseEventData(e);
      this.tool.onMouseOut(mouseInputData);
    }
  }

  private onMouseOut(e: MouseEvent) {
    this.mouseInBounds = false;
    if (this.tool) {
      const mouseInputData = this.inputEventDataExtractor.extractMouseEventData(e);
      this.tool.onMouseOut(mouseInputData);
    }
  }

  private async onMouseUp(e: MouseEvent) {
    if (this.doubleClick) {
      return;
    }

    const mouseInputData = this.inputEventDataExtractor.extractMouseEventData(e);

    if (
      this.handleContextMenu(mouseInputData) ||
      (this.tool && (await this.tool.onMouseUp(mouseInputData)))
    ) {
      await this.tools.camera.onMouseUp(mouseInputData);
    }
  }

  private async onGlobalMouseUp(e: MouseEvent) {
    // Note: Prevents the global event from triggering when event triggers on canvas.
    if (e.target === this.domElement) {
      return;
    }

    const mouseInputData = this.inputEventDataExtractor.extractMouseEventData(e);
    await this.tools.camera.onMouseUp(mouseInputData);
  }

  private onMouseWheel(e: WheelEvent) {
    e.preventDefault();
    const mouseInputData = this.inputEventDataExtractor.extractMouseEventData(e);
    if (this.tool && this.tool.onMouseWheel(mouseInputData)) {
      this.tools.camera.onMouseWheel(mouseInputData);
    }

    this.dispatchEvent({ type: 'animate' });
    this.dispatchEvent({ type: 'updateUI' });
  }

  private addTool(tool: Tool) {
    this.tools[tool.name] = tool;
    this.add(tool);
  }
}
